import { useCallback, useState, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import type { KitchenHoldIn } from "../../../../api/billKitchenHold";
import { useIsEdgeLocal, useOnEdgeModeChange } from "../../../../hooks/useEdgeMode";
import { useOrderingMessage } from "../useOrderingMessage";
import {
  clearOrderItemsHoldTime,
  commonHoldTimeOf,
  kitchenHoldPayloadOf,
  setOrderItemsHoldTime,
  type KitchenHoldTime,
} from "./kitchenHoldItems";

/** Времето за задържане в „Преглед на поръчката" — еднакво за десктоп
 *  панела и за sheet-а на телефон (`OrderPanelContent`). */
export interface KitchenHoldControls {
  /** Времето за всички — по подразбиране „Сега"; новите продукти го взимат. */
  defaultTime: KitchenHoldTime;
  /** Какво пише в заглавието: общото време на редовете или „mixed". */
  headerTime: KitchenHoldTime | "mixed";
  /** „Едно време за всички редове" под заглавието е разгънато. */
  allPickerOpen: boolean;
  onToggleAllPicker: () => void;
  /** Едно време за всички редове (и за добавените след това); свива избора. */
  onPickAll: (time: KitchenHoldTime) => void;
  /** Кой ред е с разгънат избор — първият му индекс в кошницата; null — никой. */
  openRowIndex: number | null;
  onToggleRowPicker: (rowIndex: number) => void;
  /** Времето на ред (обобщеният — всичките му записи); свива избора. */
  onPickRow: (indices: number[], time: KitchenHoldTime) => void;
  /** „Недостъпно без интернет" в локален режим — касата не задържа. */
  lockedTooltip?: string;
}

interface UseKitchenHoldChoiceArgs<OrderItem extends { kitchenHoldTime?: KitchenHoldTime }> {
  /** Кошницата — времето на ред живее в самите записи (`kitchenHoldTime`). */
  orderItems: OrderItem[];
  setOrderItems: Dispatch<SetStateAction<OrderItem[]>>;
}

/**
 * Времето за задържане за следващото „Поръчай" (state-ът живее в `Ordering`).
 * Нулира се след всяка поръчка и при отказ от кошницата (`reset`). Без интернет
 * задържане няма (касата отказва полето, а при влизане в локален режим сама
 * пуска задържаните): изборът е заключен, а вече направен — отпада с
 * предупреждение — следващото „Поръчай" пуска всичко веднага и сервитьорът го знае.
 */
export const useKitchenHoldChoice = <OrderItem extends { kitchenHoldTime?: KitchenHoldTime }>({
  orderItems,
  setOrderItems,
}: UseKitchenHoldChoiceArgs<OrderItem>) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const edgeLocal = useIsEdgeLocal();
  const [defaultTime, setDefaultTime] = useState<KitchenHoldTime>("now");
  const [allPickerOpen, setAllPickerOpen] = useState(false);
  // Разгънатият ред + колко записа имаше кошницата тогава: добавен или махнат
  // запис мести индексите — тогава изборът се затваря, вместо да се отвори
  // под чужд ред.
  const [openRow, setOpenRow] = useState<{ index: number; itemCount: number } | null>(null);
  const openRowIndex =
    openRow !== null && openRow.itemCount === orderItems.length ? openRow.index : null;
  const setOpenRowIndex = (index: number | null) =>
    setOpenRow(index === null ? null : { index, itemCount: orderItems.length });

  const reset = useCallback(() => {
    setDefaultTime("now");
    setAllPickerOpen(false);
    setOpenRow(null);
    setOrderItems(clearOrderItemsHoldTime);
  }, [setOrderItems]);

  useOnEdgeModeChange(() => {
    if (!edgeLocal) return;
    if (kitchenHoldPayloadOf(orderItems, defaultTime))
      message.warning(t("ordering.kitchenHold.offlineDropped"));
    reset();
  });

  const onToggleAllPicker = () => {
    setOpenRowIndex(null);
    setAllPickerOpen((open) => !open);
  };

  const onPickAll = (time: KitchenHoldTime) => {
    setDefaultTime(time);
    setOrderItems(clearOrderItemsHoldTime);
    setAllPickerOpen(false);
    setOpenRowIndex(null);
  };

  const onToggleRowPicker = (rowIndex: number) => {
    setAllPickerOpen(false);
    setOpenRowIndex(openRowIndex === rowIndex ? null : rowIndex);
  };

  const onPickRow = (indices: number[], time: KitchenHoldTime) => {
    setOrderItems((current) => setOrderItemsHoldTime(current, indices, time, defaultTime));
    setOpenRowIndex(null);
  };

  const controls: KitchenHoldControls = {
    defaultTime,
    headerTime: commonHoldTimeOf(orderItems, defaultTime),
    allPickerOpen,
    onToggleAllPicker,
    onPickAll,
    openRowIndex,
    onToggleRowPicker,
    onPickRow,
    lockedTooltip: edgeLocal ? t("edge.unavailableOffline") : undefined,
  };

  return {
    /** Какво да пътува с `POST /bills` (`kitchenHold`); никога в локален режим. */
    payload: (edgeLocal ? undefined : kitchenHoldPayloadOf(orderItems, defaultTime)) as
      | KitchenHoldIn[]
      | undefined,
    reset,
    controls,
  };
};
